import { type ButtonProps } from '@/components/buttons';
import { GetPageBannerBySlugQuery, useGetPageBannerBySlugQuery } from './generated';
import { serialize } from 'next-mdx-remote/serialize';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { ReactNode } from 'react';
import { sanityLanguage } from '@/constants/language';

const _slugs = [
	'about-us',
	'card',
	'career',
	'css-program',
	'deferred-payment',
	'exchange-rate',
	'extended-warranty',
	'for-developers',
	'for-eshops',
	'for-journalists',
	'homepage',
	'insurance',
	'installments',
	'investment',
	'mallpay',
	'mobile-app',
	'money-transfer',
	'openapi',
	'packages',
	'pay-in-three',
	'postpone-due-date',
	'pricing',
	'referral',
	'retention',
	'rewards',
	'revolving-loan',
	'shop-wisely',
	'testimonials',
	'welcome-video',
	'xmas',
	'spring-offer',
	'loan',
	'insurance-and-warranty',
] as const;

// Always sync with https://gitlab.com/mallpay/frontend/cms/-/blob/main/schemas/pageBanner.ts slugs
type PageBannerSlug = (typeof _slugs)[number];
export type PageBannerVariant =
	| 'homepage'
	| 'mascots'
	| 'products'
	| 'video'
	| 'mockupMobile'
	| 'mockupDesktop';

export type PageBanner = {
	title: MDXRemoteSerializeResult | string;
	text: MDXRemoteSerializeResult | string | ReactNode;
	buttons: (ButtonProps & { label: string; variant: string })[] | null;
	highlights?:
		| {
				title: string;
				text: string;
		  }[]
		| null
		| undefined;
	image: {
		url: string;
		alt: string;
	} | null;
	videoId?: string | null;
	promoSource?: string | null;
	variant: PageBannerVariant;
};

export type PageBannerBySlug = NonNullable<
	NonNullable<GetPageBannerBySlugQuery['allPageBanner']>[number]
>;
async function formatBanner(banner: PageBannerBySlug): Promise<PageBanner | null> {
	if (!banner || !banner.title || !banner.text) {
		return null;
	}
	return {
		title: await serialize(banner.title),
		promoSource: banner.promoSource,
		text: await serialize(banner.text),
		buttons:
			banner.buttons
				?.map((button, index) => {
					const id = `banner${index === 0 ? 'Primary' : 'Secondary'}`;
					if (!button || !button.title || !button.url) {
						return null;
					}
					return {
						analyticsParam: id,
						testId: id,
						ariaLabel: button.title,
						to: button.url,
						target: button.target ?? undefined,
						label: button.title,
						variant: button.variant || 'primary',
					};
				})
				.filter(Boolean) || null,
		highlights:
			banner.highlights &&
			banner.highlights
				.map((highlight) => {
					if (!highlight || !highlight.text || !highlight.title) {
						return null;
					}

					return {
						title: highlight.title || '',
						text: highlight.text || '',
					};
				})
				.filter(Boolean),
		variant: (banner.type as PageBannerVariant) || 'homepage',
		videoId: banner.videoId,
		image: banner.image?.asset?.url
			? {
					alt: banner.imageAlt || '',
					url: banner.image.asset.url,
				}
			: null,
	};
}

export async function getPageBannerBySlug(slug: PageBannerSlug): Promise<PageBanner | null> {
	const fetcher = useGetPageBannerBySlugQuery.fetcher({
		slug,
		i18nLang: sanityLanguage,
	});

	const data = await fetcher();

	return formatBanner(data.allPageBanner[0]);
}

export async function getPageBannerWithPromoSourceBySlug(
	slug: PageBannerSlug
): Promise<PageBanner[] | null> {
	const fetcher = useGetPageBannerBySlugQuery.fetcher({
		slug,
		i18nLang: sanityLanguage,
	});

	const data = await fetcher();

	const banners = await Promise.all(
		data.allPageBanner.map(async (bannerData) => {
			return await formatBanner(bannerData);
		})
	);

	return (banners.filter(Boolean) as PageBanner[]) || null;
}
